import { HttpClient, HttpHeaders, type HttpResponse } from '@angular/common/http';
import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { type Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { type ClientPlatform, type Translation, type TranslationUpsertRequest } from '../../modules/master-data/translations/translation.model';
import { type Page } from '../../shared/models/common.models';
import { type ManifestScope } from '../models/manifest';

@Injectable({ providedIn: 'root' })
export class TranslationApiService {
  private http = inject(HttpClient);

  private baseUrl = `${environment.apiBaseUrl}/api/i18n`;

  get(id: string): Observable<Translation> {
    return this.http.get<Translation>(`${this.baseUrl}/get/${id}`);
  }

  list(
    platform: ClientPlatform,
    industryType: string,
    lang: string,
    active: boolean | string,
    page: number,
    size: number,
    sort?: string[]
  ): Observable<Page<Translation>> {
    let params = new HttpParams()
      .set('platform', platform)
      .set('industryType', industryType)
      .set('lang', lang)
      .set('active', active.toString())
      .set('page', page.toString())
      .set('size', size.toString());

    if (sort && sort.length > 0) {
      sort.forEach(s => {
        params = params.append('sort', s);
      });
    }

    return this.http.get<Page<Translation>>(`${this.baseUrl}/find-all-paged`, { params });
  }

  upsert(request: TranslationUpsertRequest): Observable<Translation> {
    return this.http.post<Translation>(`${this.baseUrl}/upsert`, request);
  }

  deactivate(id: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/hide/${id}`);
  }

  exportToJson(translation: Translation): void {
    const dataStr = JSON.stringify(translation.data, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

    const exportFileDefaultName = `${translation.meta.scope}_${translation.meta.lang}_${translation.meta.platform}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  }

  importFromJson(file: File): Promise<Record<string, string>> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const data = JSON.parse(e.target.result);
          resolve(data);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = error => reject(error);
      reader.readAsText(file);
    });
  }

  getManifest(platform: 'WEB' | 'MOBILE', industryType: string, lang: string): Observable<{ scopes: ManifestScope[] }> {
    const params = {
      platform,
      industryType: industryType,
      lang
    };

    return this.http.get<{ scopes: ManifestScope[] }>(`${this.baseUrl}/manifest`, { params });
  }

  getScope(
    platform: 'WEB' | 'MOBILE',
    industryType: string,
    lang: string,
    scope: string,
    clientVersion?: string,
    etag?: string
  ): Observable<HttpResponse<any>> {
    let params = new HttpParams().set('platform', platform).set('industryType', industryType).set('lang', lang).set('scope', scope);

    if (clientVersion) {
      params = params.set('clientVersion', clientVersion);
    }

    let headers = new HttpHeaders();
    if (etag) {
      headers = headers.set('If-None-Match', etag);
    }

    return this.http.get<any>(`${this.baseUrl}/translations`, {
      observe: 'response',
      params,
      headers
    });
  }
}
